import { useFormContext } from 'react-hook-form';
import { Icon } from '@scripsteam/scrips-signal-ds';
import type { AppointmentFormValues } from '@/types/appointment-form';

interface RecurrenceSectionProps {
  /** Opens the full-screen Recurrence configurator (Flutter
   * `recurrence_appointment_widget.dart`). */
  onEdit?: () => void;
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmtDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${MONTH_SHORT[d.getMonth()]} ${String(d.getDate()).padStart(2, '0')}, ${d.getFullYear()}`;
}

/**
 * RecurrenceSection — selected-recurrence summary card. Mirrors Flutter
 * `recurrence_widget.dart`: shows "Recurrence • occurrence X of Y" with
 * the start/end date span, an edit pencil that opens the configurator,
 * and a × that clears the recurrence. Hidden when `recurring === null`.
 */
export function RecurrenceSection({ onEdit }: RecurrenceSectionProps = {}) {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const recurring = watch('recurring');

  if (!recurring) return null;

  const { startDate, endDate, repeatEvery, numberOfRepeat, period, days } = recurring;
  const periodSingular = (period ?? 'Days').replace(/s$/, '').toLowerCase();
  const summary = `Every ${repeatEvery} ${periodSingular}${repeatEvery === 1 ? '' : 's'} · ${numberOfRepeat} occurrence${numberOfRepeat === 1 ? '' : 's'}`;
  const dayLine =
    period === 'Weeks' && days?.length
      ? days.map((d) => d.slice(0, 3)).join(' · ')
      : null;

  return (
    <div className="booking-section" data-slot="booking-section">
      <p className="booking-section-label">
        Recurrence
      </p>
      <div className="booking-card flex items-start">
        <Icon name="refresh" size={20} aria-hidden />
        <div className="flex-1 min-w-0">
          <p className="booking-card-title">
            {summary}
          </p>
          <p className="booking-card-meta">
            {fmtDate(startDate)} — {fmtDate(endDate)}
          </p>
          {dayLine ? (
            <p className="booking-card-meta">{dayLine}</p>
          ) : null}
        </div>
        {onEdit ? (
          <button type="button" aria-label="Edit recurrence" onClick={onEdit} className="booking-icon-btn"><Icon name="edit" size={16} aria-hidden /></button>
        ) : null}
        <button type="button" aria-label="Clear recurrence" onClick={() => setValue('recurring', null, { shouldValidate: true })} className="booking-icon-btn"><Icon name="close" size={16} aria-hidden /></button>
      </div>
    </div>
  );
}
